import { type AxiosInstance } from 'axios';
import {
  BoxPlotResponse,
  FundsAdjustmentResponse,
  GROUP_BY,
  IGeneralStatsResponse,
  IIndexQuotes,
  IMovableWindowSeries,
  IQuotedSimulationResponse,
  IWalletSimulationBody,
  PERIOD_BY,
  SuccessRateResponse,
  WindowAccumulatedReturnResponse,
} from './interface';
import {
  IGetAccumulatedReturnParams,
  IQuoteParams,
  IPortfolioParams,
  IPortfolioTransactionParams,
  GetIssuersResponse,
  GetBrokersResponse,
  MakeTransactionResponse,
  GetBrokersStrapiResponse,
} from './interfaces/props';
import { CorrelationBody, CorrelationResponse } from './interface';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  IPortfolioCreateResponse,
  IPortfolioListResponse,
  IGetPortfolioResponse,
  IPortfolioTransactionResponse,
  IUploadTransactionsResponse,
  IPortfolioProfitAssetsResponse,
  IPortfolioNetWorthResponse,
  IPortfolioCompositionResponse,
  IPortfolioDividendsByAssetResponse,
  IPortfolioDividendsByYearResponse,
  IPortfolioDividendsResponse,
  IPortfolioIncomesResponse,
  IPortfolioTransactionVerifyResponse,
  IPortfolioHasB3DocumentResponse,
  IPortfolioGetDocumentResponse,
  IPortfolioGetRealIncomeResponse,
  PortfolioUploadTransactionType,
  PortfolioApplicationTag,
  IPortfolioJobResponse,
  PortfolioJobsParams,
  IPortfolioQuotesResponse,
  IPortfolioAssetsPositionsResponse,
  IApproveTransactionParams,
  IGetCustomerToSharePortfolioResponse,
  IGetIntegrationFromCustomerResponse,
  IGetTeamPortfolioResponse,
  GetPortfolioComeCotasResponse,
  IPortfolioTransactionsByMonthsResponse,
} from './interfaces/portfolio';
import {
  IFundHeader,
  IFundStatsFromRelativeBenchmark,
} from './interfaces/funds';
import {
  IGeneralHeader,
  IGeneralQuotes,
  IGeneralSearch,
  IGeneralSearchParams,
  IQuote,
} from './interfaces/general';
import { IFundStats } from './interfaces/stats';
import {
  IAdminAndAssetManagerResponse,
  IAssetManager,
} from './interfaces/administrator';
import {
  ComparisonProps,
  ComparisonResponse,
  CreateComparisonResponse,
  ListComparisonsParams,
} from './interfaces/comparison';
import { ISaveCacheParams } from './interfaces/userdata';
import { TeamTheme, UserProfile } from './interfaces/team';
import {
  ISheetFundClassesResponse,
  ISheetFundClassSummaryResponse,
  ISheetFundSubclassesIdentifierResponse,
  ISheetFundSummaryResponse,
  ISheetResponse,
} from './interfaces/sheet';

dayjs.extend(utc);

export class MarketDataAPI {
  private baseUrl: string;

  constructor(public readonly http: AxiosInstance) {
    this.baseUrl = this.http.defaults.baseURL || '';
  }

  setVersion(version: string = 'v3') {
    if (!this.baseUrl) {
      throw new Error('Base URL is not set');
    }

    this.baseUrl = this.baseUrl!.replace(/\/v\d+/, `/${version}`);

    return this;
  }

  setDomain(domain: string) {
    this.baseUrl = this.baseUrl!.replace(/\/api\d*/, `/${domain}`);

    return this;
  }

  public sheet = {
    getSheet: <T extends ISheetResponse>(
      canonical: string,
      props?: Partial<Params>
    ) =>
      this._makeRequest<T>({
        endpoint: `/general/sheet`,
        method: 'GET',
        params: { canonical },
        ...(props && props),
      }),
    getFundSummary: (cnpj: string) =>
      this._makeRequest<ISheetFundSummaryResponse>({
        endpoint: `/fund/fund-summary/${cnpj}`,
        method: 'GET',
      }),
    getFundClassSummary: (cnpj: string) =>
      this._makeRequest<ISheetFundClassSummaryResponse>({
        endpoint: `/fund/subclass-summary/${cnpj}`,
        method: 'GET',
      }),
    getFundDetailsSummary: (identifier: string) =>
      this._makeRequest<ISheetFundSummaryResponse>({
        endpoint: `/fund/subclass-summary/${identifier}`,
        method: 'GET',
      }),
    getFundSubclassesIdentifier: (cnpj: string) =>
      this._makeRequest<ISheetFundSubclassesIdentifierResponse>({
        endpoint: `/fund/subclasses/${cnpj}`,
        method: 'GET',
        safe: true,
      }),
    getFundClasses: (cnpj: string) =>
      this._makeRequest<ISheetFundClassesResponse>({
        endpoint: `/fund/classes/${cnpj}`,
        method: 'GET',
        safe: true,
      }),
  };

  public funds = {
    search: (term: string) =>
      this._makeRequest({ endpoint: `/funds/search/${term}`, method: 'GET' }),
    getHeaderComparison: (identifier: string) =>
      this._makeRequest({
        endpoint: `/funds/header/${identifier}/comparison`,
        method: 'GET',
      }),
    getHeaderFull: (identifier: string, params?: Partial<Params>) =>
      this._makeRequest<IFundHeader>({
        ...params,
        endpoint: MarketDataAPI.ROUTES.funds.header(identifier)!,
        method: 'GET',
      }),
    getQuotes: (identifier: string) =>
      this._makeRequest({
        endpoint: `/funds/quotes/${identifier}`,
        method: 'GET',
      }),
    getPolicies: (identifier: string) =>
      this._makeRequest({
        endpoint: `/funds/policies/${identifier}`,
        method: 'GET',
      }),
    stats: {
      getStatsComparison: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/stats/${identifier}/comparison`,
          method: 'GET',
        }),
      getStatsDetails: (identifier: string, params?: Partial<Params>) =>
        this._makeRequest<IFundStats>({
          ...params,
          endpoint: `/funds/stats/${identifier}/details`,
          method: 'GET',
        }),
      getRelativeComparison: (identifier: string, index: string) =>
        this._makeRequest({
          endpoint: `/funds/stats/${identifier}/relative/${index}/comparison`,
          method: 'GET',
        }),
    },
    manager: {
      getFundsAdjustments: (identifier: string) =>
        this._makeRequest<FundsAdjustmentResponse>({
          endpoint: `/funds/adjustment/${identifier}`,
          method: 'GET',
        }),
    },
    wallet: {
      getAvailableMonths: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/wallet/${identifier}/available-months`,
          method: 'GET',
        }),
      getAvailableDates: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/wallet/${identifier}/available`,
          method: 'GET',
        }),
      getWalletSummary: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/wallet/${identifier}`,
          method: 'GET',
        }),
      getHistoricalWallet: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/wallet/${identifier}/historical`,
          method: 'GET',
        }),
      getDetailedWallet: (identifier: string) =>
        this._makeRequest({
          endpoint: `/funds/wallet/${identifier}/details`,
          method: 'GET',
        }),
    },
    getLastBaseDate: () =>
      this._makeRequest<{ date: string }>({
        endpoint: `/funds/last-base-date`,
        method: 'GET',
      }),
    getFundBenchmarkRelativeStats: ({
      cnpj,
      benchmark,
      ...params
    }: {
      cnpj: string;
      benchmark: string;
    } & Partial<Params>) =>
      this._makeRequest<IFundStatsFromRelativeBenchmark>({
        ...params,
        endpoint: `/indexes/stats/${cnpj}/relative/${benchmark}/details`,
        method: 'GET',
      }),
  };

  public indexes = {
    getList: () => this._makeRequest({ endpoint: '/indexes/', method: 'GET' }),
    getHeaderFull: (identifier: string) =>
      this._makeRequest({
        endpoint: `/indexes/header/${identifier}/full`,
        method: 'GET',
      }),
    getRelated: (identifier: string) =>
      this._makeRequest({
        endpoint: `/indexes/related/${identifier}`,
        method: 'GET',
      }),
    search: (term: string) =>
      this._makeRequest({ endpoint: `/indexes/search/${term}`, method: 'GET' }),
    getQuotes: (identifier: string) =>
      this._makeRequest<IIndexQuotes>({
        endpoint: `/indexes/quotes/${identifier}`,
        method: 'GET',
      }),
    stats: {
      getStatsComparison: (identifier: string) =>
        this._makeRequest({
          endpoint: `/indexes/stats/${identifier}/comparison`,
          method: 'GET',
        }),
      getStatsDetails: (index: string) =>
        this._makeRequest({
          endpoint: MarketDataAPI.ROUTES.indexes.stats(index)!,
          method: 'GET',
        }),
      getRelativeDetails: (identifier: string, index: string) =>
        this._makeRequest({
          endpoint: `/indexes/stats/${identifier}/relative/${index}/details`,
          method: 'GET',
        }),
    },
  };

  public administrators = {
    search: (term: string) =>
      this._makeRequest<IAdminAndAssetManagerResponse>({
        endpoint: `/admins/search/${term}`,
        method: 'GET',
      }),
  };

  public assetManagers = {
    getManager: (slug: string) =>
      this._makeRequest<IAssetManager>({
        endpoint: `/asset-managers/details/${slug}`,
        method: 'GET',
      }),
    search: (term: string) =>
      this._makeRequest<IAdminAndAssetManagerResponse>({
        endpoint: `/asset-managers/search/${term}`,
        method: 'GET',
      }),
  };

  public general = {
    search: (term: string, params?: IGeneralSearchParams) => {
      const termWithoutSlash = term.replaceAll('/', ' ');

      return this._makeRequest<IGeneralSearch[]>({
        endpoint: `/general/search/${termWithoutSlash}`,
        method: 'GET',
        params,
      });
    },
    window: {
      getAccumulatedReturn: (
        identifier: string,
        params: IGetAccumulatedReturnParams
      ) =>
        this._makeRequest<WindowAccumulatedReturnResponse>({
          endpoint: `/ms/calc/window/${identifier}/accumulated-return`,
          method: 'GET',
          params,
        }),
      getBoxplot: (identifier: string) =>
        this._makeRequest<BoxPlotResponse>({
          endpoint: `/ms/calc/window/${identifier}/boxplot`,
          method: 'GET',
        }),
      getSuccessRate: (
        identifier: string,
        benchmark?: string,
        currency?: string
      ) =>
        this._makeRequest<SuccessRateResponse>({
          endpoint: `/ms/calc/window/${identifier}/success-rate`,
          method: 'GET',
          params: { benchmark, currency },
        }),
      getAccumulatedReturnByQuotes: (
        params: IGetAccumulatedReturnParams,
        body: IMovableWindowSeries
      ) =>
        this._makeRequest<WindowAccumulatedReturnResponse>({
          endpoint: `/ms/calc/window/-/accumulated-return`,
          method: 'POST',
          params,
          body,
        }),
      getSuccesRateByQuotes: (body: IMovableWindowSeries, benchmark?: string) =>
        this._makeRequest<SuccessRateResponse>({
          endpoint: `/ms/calc/window/-/success-rate`,
          method: 'POST',
          body,
          params: { benchmark },
        }),
    },
    getQuotes: async ({
      identifier,
      adjusted,
      startDate,
      endDate,
      currency,
      ...rest
    }: IQuoteParams) => {
      const params = {
        adjusted: adjusted === false ? 'false' : 'true',
        currency,
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await this._makeRequest<IGeneralQuotes>({
        ...rest,
        endpoint: `/general/quotes/${identifier}`,
        method: 'GET',
        params,
      });

      if (!response) return null;
      return { identifier, ...response, id: response?.id || identifier };
    },
    getStats: async ({
      identifier,
      startDate,
      endDate,
      format_decimal = false,
      currency,
      ...config
    }: IQuoteParams) => {
      const params = {
        currency,
        format_decimal,
        ...(startDate && {
          start_date: dayjs.utc(startDate).valueOf(),
        }),
        ...(endDate && {
          end_date: dayjs.utc(endDate).valueOf(),
        }),
      };

      const response = await this._makeRequest<IGeneralStatsResponse>({
        endpoint: `/general/stats/${identifier}`,
        method: 'GET',
        params,
        ...config,
      });

      if (!response) return null;
      return { identifier, ...response };
    },
    getCorrelationMatrix: (body: CorrelationBody) =>
      this._makeRequest<CorrelationResponse>({
        endpoint: '/ms/correlation-matrix',
        method: 'POST',
        body: {
          props: { period: 'otimo', start_date: null, end_date: null },
          ...body,
        },
      }),
    getStatistics: (body: IQuote[], params?: Partial<Params>) =>
      this._makeRequest<IGeneralStatsResponse>({
        endpoint: '/ms/statistics',
        method: 'POST',
        body,
        ...(params && params),
      }),
    getHeader: (identifier: string, params?: Partial<Params>) =>
      this._makeRequest<IGeneralHeader>({
        endpoint: `/general/header/${identifier}`,
        method: 'GET',
        ...(params && params),
      }),
  };

  public simulation = {
    quote: (body: IWalletSimulationBody) => {
      return this._makeRequest<IQuotedSimulationResponse>({
        endpoint: `/ms/simulation?validate_only=${body.validation || false}`,
        method: 'POST',
        body,
      });
    },
    /**
     * Salva uma versao cotizada da carteira para o compartilhamento
     * @returns void
     */
    saveCache: ({
      identifier,
      cache,
      ...rest
    }: ISaveCacheParams): Promise<void> =>
      this._makeRequest({
        endpoint: `/income-simulator/${identifier}/cache`,
        method: 'PUT',
        body: cache,
        ...rest,
      }),
    validation: ({ body }: { body: any }) => {
      return this._makeRequest({
        endpoint: '/internal/wallet/validation',
        method: 'POST',
        body,
      });
    },
  };

  public portfolio = {
    deletePortfolio: ({ portfolioId }: IPortfolioParams) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.portfolio.portfolioResource(portfolioId),
        method: 'DELETE',
      }),
    updatePortfolio: ({ portfolioId, name, config }: IPortfolioParams) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.portfolio.portfolio,
        method: 'PUT',
        body: { name, config },
        params: { portfolio_id: portfolioId },
      }),

    createPortfolioSettings: ({
      key,
      value,
      portfolio_id,
    }: {
      portfolio_id: string;
      key: string;
      value: any;
    }) => {
      return this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.portfolio.portfolioSettings,
        method: 'POST',
        body: {
          key,
          value,
        },
        params: {
          portfolio_id,
        },
      });
    },
    createPortfolio: ({
      name,
      tag,
    }: {
      name: string;
      tag: PortfolioApplicationTag;
    }) =>
      this._makeRequest<IPortfolioCreateResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.portfolio,
        method: 'POST',
        body: {
          name,
          plan_tag: tag,
        },
      }),

    getPortfolio: async ({ id, safe }: { id: string; safe?: boolean }) => {
      const res = await this._makeRequest<IGetPortfolioResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.portfolioResource(id),
        method: 'GET',
        safe,
      });

      if (!res) return null;

      return {
        id,
        ...res,
      };
    },
    getNetworth: async ({ portfolioId }: { portfolioId: string }) => {
      const networth = await this._makeRequest<IPortfolioNetWorthResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.networth,
        method: 'GET',
        params: { portfolio_id: portfolioId },
      });

      return networth;
    },
    getComposition: async ({ portfolioId }: { portfolioId: string }) => {
      const composition =
        await this._makeRequest<IPortfolioCompositionResponse>({
          endpoint: MarketDataAPI.ROUTES.portfolio.composition,
          method: 'GET',
          params: { portfolio_id: portfolioId },
        });

      return composition;
    },
    getDividendsByYears: async ({ portfolioId }: { portfolioId: string }) => {
      const dividendsByYears =
        await this._makeRequest<IPortfolioDividendsByYearResponse>({
          endpoint: MarketDataAPI.ROUTES.portfolio.dividendsByYears,
          method: 'GET',
          params: { portfolio_id: portfolioId },
        });

      return dividendsByYears;
    },
    reConsolidatePortfolio: ({
      portfolioId,
      document,
      date,
    }: {
      portfolioId: string;
      document?: string;
      date?: string;
    }) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.portfolio.reConsolidate,
        params: {
          portfolio_id: portfolioId,
          ...(document && { cpf: document }),
          ...(date && { date }),
        },
        method: 'POST',
      }),
    getPortfolioHasTransaction: async ({
      portfolioId,
    }: {
      portfolioId: string;
    }) => {
      const data = await this._makeRequest<IPortfolioTransactionVerifyResponse>(
        {
          endpoint: MarketDataAPI.ROUTES.portfolio.verifyHasTransactions,
          params: { portfolio_id: portfolioId },
          method: 'GET',
        }
      );

      return data.transactions;
    },
    getPortfolioTransactions: async ({
      portfolioId,
      params,
    }: {
      portfolioId: string;
      params: {
        page?: number;
        limit?: number;
        name?: string;
        date?: string;
        needs_review?: boolean;
        [key: string]: any;
      };
    }) => {
      const data = await this._makeRequest<IPortfolioTransactionResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.transactions,
        params: { portfolio_id: portfolioId, ...params },
        method: 'GET',
      });

      return data;
    },
    list: ({
      tag,
      search,
    }: {
      tag: PortfolioApplicationTag;
      search?: string;
    }) =>
      this._makeRequest<IPortfolioListResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.list,
        method: 'GET',
        params: { tag, search },
      }),
    getIssuers: () =>
      this._makeRequest<GetIssuersResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.issuer,
        method: 'GET',
      }),
    getBrokers: () =>
      this._makeRequest<GetBrokersResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.brokers,
        method: 'GET',
      }),
    getBrokersStrapi: () =>
      this._makeRequest<GetBrokersStrapiResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.brokersStrapi,
        method: 'GET',
      }),
    getFixedIncomesByPortfolio: ({
      portfolioId,
      safe = true,
    }: Partial<Params> & { portfolioId: string }) =>
      this._makeRequest<IPortfolioGetRealIncomeResponse>({
        endpoint: `/pcs/asset_nicename`,
        method: 'GET',
        params: { portfolio_id: portfolioId },
        safe,
      }),
    createTransaction: ({ portfolioId, body }: IPortfolioTransactionParams) =>
      this._makeRequest<MakeTransactionResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.transactions,
        method: 'POST',
        body,
        params: { portfolio_id: portfolioId },
      }),
    profitAssets: ({ portfolioId }: { portfolioId: string }) =>
      this._makeRequest<IPortfolioProfitAssetsResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.profitAssets,
        method: 'GET',
        params: { portfolio_id: portfolioId },
      }),
    getQuotes: (portfolioId: string, params: Partial<Params>) =>
      this._makeRequest<IPortfolioQuotesResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.profit,
        method: 'GET',
        params: { portfolio_id: portfolioId },
        ...(params && params),
      }),
    duplicatePortfolio: ({ portfolioId }: { portfolioId: string }) =>
      this._makeRequest({
        endpoint:
          MarketDataAPI.ROUTES.portfolio.duplicatePortfolio(portfolioId),
        method: 'POST',
      }),

    deleteTransaction: ({
      portfolioId,
      transactionId,
    }: {
      transactionId: number;
      portfolioId: string;
    }) =>
      this._makeRequest({
        endpoint: `/pcs/transaction`,
        method: 'DELETE',
        params: { portfolio_id: portfolioId, transaction_id: transactionId },
      }),
    uploadTransactions: ({
      portfolioId,
      file,
      fileType,
    }: {
      portfolioId: string;
      file: any;
      fileType: PortfolioUploadTransactionType;
    }) => {
      const formData = new FormData();
      formData.append('arquivo', file);

      return this._makeRequest<IUploadTransactionsResponse>({
        endpoint:
          fileType === PortfolioUploadTransactionType.ASSET
            ? '/pcs/transaction_upload'
            : '/pcs/rfs_upload',
        method: 'POST',
        body: formData,
        params: { portfolio_id: portfolioId },
        headers: {
          accept: 'multipart/form-data',
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    resetPortfolio: ({ portfolioId }: { portfolioId: string }) =>
      this._makeRequest<void>({
        method: 'DELETE',
        endpoint: MarketDataAPI.ROUTES.portfolio.resetPortfolio,
        params: { portfolio_id: portfolioId },
      }),
    deleteBulkTransactions: ({
      portfolioId,
      transactionIds,
    }: {
      portfolioId: string;
      transactionIds: number[];
    }) =>
      this._makeRequest({
        endpoint: '/pcs/bulk_transaction_delete',
        method: 'DELETE',
        params: { portfolio_id: portfolioId, transactions: transactionIds },
      }),
    b3: {
      portfolioHasDocument: ({ portfolioId }: { portfolioId: string }) =>
        this._makeRequest<IPortfolioHasB3DocumentResponse>({
          endpoint:
            MarketDataAPI.ROUTES.portfolio.b3.portfolioHasDocument(
              portfolioId
            )!,
          method: 'GET',
        }),
      linkDocumentWithPortfolio: ({
        document,
        portfolioId,
      }: {
        document: string;
        portfolioId: string;
      }) => {
        const cleanDocument = document.replace(/\D/g, '');

        return this._makeRequest({
          endpoint: `/pcs/relate-cpf-portfolio/${portfolioId}/${cleanDocument}`,
          method: 'POST',
        });
      },
      unlinkDocumentWithPortfolio: ({
        document,
        portfolioId,
      }: {
        document: string;
        portfolioId: string;
      }) => {
        const cleanDocument = document.replace(/\D/g, '');

        return this._makeRequest({
          endpoint: MarketDataAPI.ROUTES.portfolio.b3.disconnect(
            portfolioId,
            cleanDocument
          ),
          method: 'DELETE',
        });
      },
      getPortfolioOwnerDocument: ({ portfolioId }: { portfolioId: string }) =>
        this._makeRequest<IPortfolioGetDocumentResponse>({
          endpoint:
            MarketDataAPI.ROUTES.portfolio.portfolioDocumentOwner(portfolioId),
          method: 'GET',
        }),
    },
    export: (
      portfolio_id: string,
      format: 'xlsx' | 'csv',
      exportType: 'composition' | 'transactions'
    ) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.portfolio[exportType],
        method: 'GET',
        params: { portfolio_id, format },
        config: { responseType: 'blob' },
      }),
    getJobs: ({ portfolio_id, page, limit }: PortfolioJobsParams) =>
      this._makeRequest<IPortfolioJobResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.importLogs,
        method: 'GET',
        params: { portfolio_id, limit, page },
      }),
    getAssetPosition: (
      {
        portfolioId,
        identifier,
      }: {
        portfolioId: string;
        identifier: string;
      },
      params?: Partial<Params>
    ) =>
      this._makeRequest<IPortfolioAssetsPositionsResponse>({
        method: 'GET',
        endpoint: '/pcs/asset_quantity',
        params: { portfolio_id: portfolioId, asset_identifier: identifier },
        ...(params && params),
      }),
    aproveTransaction: ({
      portfolioId,
      isApproved,
      transactionId,
    }: IApproveTransactionParams) =>
      this._makeRequest({
        endpoint: '/pcs/transaction_approval',
        method: 'PUT',
        body: {
          id: transactionId,
          is_approved: isApproved,
        },
        params: { portfolio_id: portfolioId },
      }),

    integration: {
      inviteCustomerToSharePortfolio: ({
        portfolioId,
        customer_email,
        integrationType = 'B3',
      }: {
        portfolioId: string;
        customer_email: string;
        integrationType?: 'B3' | 'VIEWER';
      }) =>
        this._makeRequest({
          endpoint: `/portfolio/${portfolioId}/integration`,
          method: 'POST',
          body: {
            customer_email,
            integration_type: integrationType,
          },
        }),
      getUserIntegrations: () =>
        this._makeRequest<IGetIntegrationFromCustomerResponse>({
          endpoint: `/pcs/integration`,
          method: 'GET',
        }),
      getIntegrationFromCustomer: ({
        integrationId,
      }: {
        integrationId: string;
      }) =>
        this._makeRequest<IGetIntegrationFromCustomerResponse>({
          endpoint: `/portfolio/integration/${integrationId}`,
          method: 'GET',
        }),
      getCustomerToSharePortfolio: ({
        portfolioId,
        integrationType = 'B3',
      }: {
        portfolioId: string;
        integrationType?: 'B3';
      }) =>
        this._makeRequest<IGetCustomerToSharePortfolioResponse>({
          endpoint: `/portfolio/${portfolioId}/integration`,
          method: 'GET',
          params: { integration: integrationType },
        }),
      resendEmailToSharePortfolio: ({
        portfolioId,
        integrationType = 'B3',
      }: {
        portfolioId: string;
        integrationType?: 'B3';
      }) =>
        this._makeRequest<IGetCustomerToSharePortfolioResponse>({
          endpoint: `/portfolio/${portfolioId}/integration/resend-invite`,
          method: 'GET',
          params: { integration: integrationType },
        }),
      cancelIntegration: ({ integrationId }: { integrationId: string }) =>
        this._makeRequest<void>({
          endpoint: `/pcs/integration/${integrationId}`,
          method: 'DELETE',
        }),
    },
    group: {
      createGroup: ({
        portfolioId,
        portfolios,
      }: {
        portfolioId: string;
        portfolios: string[];
      }) =>
        this._makeRequest({
          endpoint: `/pcs/portfolio_hierarchy`,
          method: 'PUT',
          params: { group_portfolio_id: portfolioId },
          body: { child_portfolios: portfolios },
        }),
      removePortfolioFromGroup: ({
        portfolioId,
        portfolios,
      }: {
        portfolioId: string;
        portfolios: string[];
      }) =>
        this._makeRequest({
          endpoint: `/pcs/portfolio_hierarchy`,
          method: 'DELETE',
          params: { group_portfolio_id: portfolioId },
          body: { child_portfolios: portfolios },
        }),
    },
    getPortfolioPermission: ({ portfolio_id }: { portfolio_id: string }) => {
      return this._makeRequest<{
        canView: boolean;
        isOwner: boolean;
      }>({
        endpoint: `/pcs/can-view/${portfolio_id}`,
        method: 'GET',
      });
    },
    comeCotas: {
      getTransactions: ({
        portfolioId,
        page,
        pageSize,
      }: {
        portfolioId: string;
        page: number;
        pageSize: number;
      }) =>
        this._makeRequest<GetPortfolioComeCotasResponse>({
          endpoint: MarketDataAPI.ROUTES.portfolio.getComeCotasList,
          method: 'GET',
          params: { portfolio_id: portfolioId, page, limit: pageSize },
        }),
      approvalAll: ({
        portfolioId,
        is_approved,
      }: {
        portfolioId: string;
        is_approved: boolean;
      }) =>
        this._makeRequest({
          endpoint: `/pcs/all_come_cotas_approval`,
          method: 'PUT',
          params: { portfolio_id: portfolioId },
          body: { is_approved },
        }),
      update: ({
        portfolioId,
        transactionId,
        body,
      }: {
        portfolioId: string;
        transactionId: string;
        body: any;
      }) =>
        this._makeRequest({
          endpoint: `/pcs/come_cotas_transaction`,
          method: 'PUT',
          params: { portfolio_id: portfolioId, transaction_id: transactionId },
          body,
        }),
      approve: ({
        portfolioId,
        transactionId,
        is_approved,
      }: {
        portfolioId: string;
        transactionId?: string;
        is_approved: boolean;
      }) =>
        this._makeRequest({
          endpoint: `/pcs/come_cotas_approval`,
          method: 'PUT',
          params: { portfolio_id: portfolioId, transaction_id: transactionId },
          body: {
            is_approved,
          },
        }),
    },
    getTransactionsByMonths: ({
      portfolioId,
      period,
      date,
    }: {
      portfolioId: string;
      period: string;
      date?: string;
    }) =>
      this._makeRequest<IPortfolioTransactionsByMonthsResponse>({
        endpoint: '/pcs/transaction_period',
        method: 'GET',
        params: { portfolio_id: portfolioId, period, date },
      }),

    getPortfolioIncomesHistory: ({
      portfolioId,
      params,
    }: {
      portfolioId: string;
      params: { group?: GROUP_BY; period: PERIOD_BY; date?: string };
    }) =>
      this._makeRequest<IPortfolioIncomesResponse>({
        endpoint: '/pcs/income_total',
        method: 'GET',
        params: { portfolio_id: portfolioId, ...params },
      }),
    getPortfolioIncomesByAssets: ({
      portfolioId,
      params,
    }: {
      portfolioId: string;
      params: { period?: string; date?: string };
    }) =>
      this._makeRequest<IPortfolioDividendsByAssetResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.dividendsByAssets,
        method: 'GET',
        params: { portfolio_id: portfolioId, ...params },
      }),

    getIncomes: async ({
      portfolioId,
      params,
    }: {
      portfolioId: string;
      params: {
        page: number;
        limit: number;
        name?: string;
        date?: string;
        [key: string]: any;
      };
    }) =>
      this._makeRequest<IPortfolioDividendsResponse>({
        endpoint: MarketDataAPI.ROUTES.portfolio.incomes,
        method: 'GET',
        params: { portfolio_id: portfolioId, ...params },
      }),
  };

  public currency = {
    getCurrencies: () =>
      this._makeRequest({
        endpoint: '/ms/currency',
        method: 'GET',
      }),
    convertToCurrency: ({
      nicename,
      quotes,
      currency,
      toCurrency,
    }: {
      currency: string;
      toCurrency: string;
      quotes: IQuote[];
      nicename: string;
    }) =>
      this._makeRequest({
        endpoint: '/ms/convert-currency',
        method: 'POST',
        body: { nicename, quotes },
        params: { currency, currency_to: toCurrency },
      }),
  };

  public setAuthToken(token: string): void {
    if (token) {
      this.http.defaults.headers.common['Authorization'] = token.includes(
        'Bearer'
      )
        ? token
        : `Bearer ${token}`;
    }
  }

  public comparison = {
    createComparison: (body: ComparisonProps) =>
      this._makeRequest<CreateComparisonResponse>({
        endpoint: MarketDataAPI.ROUTES.comparison.create,
        method: 'POST',
        body,
      }),
    listComparisons: (params: ListComparisonsParams) =>
      this._makeRequest<ComparisonResponse[]>({
        endpoint: MarketDataAPI.ROUTES.comparison.get,
        method: 'GET',
        params,
      }),
    getComparisonById: (id: string) =>
      this._makeRequest<ComparisonResponse>({
        endpoint: MarketDataAPI.ROUTES.comparison.getById(id),
        method: 'GET',
      }),
    deleteComparison: (id: string) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.comparison.delete(id),
        method: 'DELETE',
      }),
    copyComparison: (id: string) =>
      this._makeRequest<CreateComparisonResponse>({
        endpoint: MarketDataAPI.ROUTES.comparison.copy(id),
        method: 'POST',
      }),
    updateComparison: (body: ComparisonProps) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.comparison.update(body.id!),
        method: 'PUT',
        body,
      }),
  };
  public team = {
    getMemberDetails: ({
      teamId,
      memberId,
    }: {
      teamId: string;
      memberId: string;
    }) =>
      this._makeRequest({
        endpoint: MarketDataAPI.ROUTES.userData.getMemberDetails(
          teamId,
          memberId
        ),
        method: 'GET',
      }),
    getUserProfile: () =>
      this._makeRequest<UserProfile>({
        endpoint: MarketDataAPI.ROUTES.userData.user_profile,
        method: 'GET',
      }),
    getTeamTheme: ({ teamId }: { teamId: string }) =>
      this._makeRequest<TeamTheme>({
        endpoint: MarketDataAPI.ROUTES.userData.team_theme(teamId),
        method: 'GET',
      }),
  };

  public user = {
    getCompleteProfile: () =>
      this._makeRequest({
        endpoint: '/users/complete-profile',
        method: 'GET',
      }),
    updateCompleteProfile: (body: {
      is_professional: boolean;
      company_name?: string;
      job_title?: string;
    }) =>
      this._makeRequest({
        endpoint: '/users/complete-profile',
        method: 'PUT',
        body,
      }),
    listTeamPortfolios: ({ teamId }: { teamId: string }) =>
      this._makeRequest<IGetTeamPortfolioResponse>({
        endpoint: `/portfolio/${teamId}/portfolios`,
        method: 'GET',
      }),
  };

  static ROUTES = {
    portfolio: {
      getComeCotasList: '/pcs/come_cotas_transactions',
      issuer: '/pcs/issuer',
      brokers: '/pcs/broker',
      brokersStrapi: '/corretoras?pagination[limit]=1000&sort[0]=name:asc',
      transactions: '/pcs/transaction',
      verifyHasTransactions: '/pcs/has_transaction',
      dividendsByYears: '/pcs/income_period',
      dividendsByAssets: '/pcs/income_asset',
      profitAssets: '/pcs/asset',
      incomes: '/pcs/income',
      profit: '/pcs/return',
      portfolioStats: '/pcs/stat',
      networth: '/pcs/networth',
      composition: '/pcs/portfolio_composition',
      portfolio: '/pcs/portfolio',
      list: '/pcs/portfolio',
      reConsolidate: '/pcs/consolidate',
      resetPortfolio: '/pcs/portfolio_reset',
      portfolioSettings: '/pcs/portfolio_setting',
      portfolioResource: (id: string) => `/pcs/portfolio/${id}`,
      portfolioDocumentOwner: (id: string) => `/pcs/portfolio-has-cpf/${id}`,
      duplicatePortfolio: (id: string) => `/pcs/portfolio_duplicate/${id}`,
      snapshots: `/pcs/portfolio_snapshot`,
      importLogs: '/pcs/job',
      b3: {
        portfolioResource: (id: string) => `/pcs/portfolio/${id}`,
        portfolioHasDocument: (portfolioId: string) =>
          portfolioId ? `/pcs/portfolio-has-cpf/${portfolioId}` : null,
        verifyB3Auth: '/pcs/b3_auth',
        disconnect: (portfolio_id: string, cpf: string) =>
          `/pcs/delete-cpf-portfolio/${portfolio_id}/${cpf}`,
      },
      portfolioPermissions: (portfolioId: string) =>
        portfolioId ? `/pcs/can-view/${portfolioId}` : null,
    },
    indexes: {
      indexes: '/indexes',
      index: '/indexes/index',
      quotes: '/indexes/quotes',
      stats: (index: string | null) =>
        index ? `/indexes/stats/${index}/details` : null,
      accumulatedReturn: '/indexes/accumulated-return',
      boxplot: '/indexes/boxplot',
      successRate: '/indexes/success-rate',
    },
    funds: {
      header: (cnpj: string | number) =>
        cnpj ? `/funds/header/${cnpj}/full` : null,
      walletSummary: (cnpj: string | number) =>
        cnpj ? `/funds/wallet/${cnpj}` : null,
    },
    general: {
      quotes: (identifier: string) => `/general/quotes/${identifier}`,
    },
    userData: {
      getMemberDetails: (teamId: string, memberId: string) =>
        `/pro/teams/${teamId}/members/${memberId}`,
      user_profile: '/pro/teams/user',
      team_theme: (teamId: string) => `/pro/teams/${teamId}/theme`,
      getWalletSimulation: (simulationId: string) =>
        `/pro/teams/wallet-simulation/${simulationId}`,
      getWalletSimulationList: `/wallet-simulation`,
      getUserProfile: () => `/pro/teams/user`,
      getIncomeSimulation: (identifier: string) =>
        `income-simulator/${identifier}`,
    },
    comparison: {
      create: '/comparison',
      get: '/comparison',
      getById: (id: string) => `/comparison/${id}`,
      delete: (id: string) => `/comparison/${id}`,
      copy: (id: string) => `/comparison/${id}/copy`,
      update: (id: string) => `/comparison/${id}`,
    },
  };

  private async _makeRequest<T>({
    endpoint,
    method,
    body = null,
    params = null,
    headers = null,
    safe = false,
    config = null,
  }: Params): Promise<T> {
    try {
      const { data } = await this.http({
        method: method,
        url: `${this.baseUrl}${endpoint}`,
        ...(body && { data: body }),
        ...(params && { params }),
        ...(headers && { headers }),
        ...(config && { ...config }),
      });
      if (data === '') return null as T;

      return data;
    } catch (error: any) {
      const requestError = {
        url: error.config.url,
        method: error.config.method,
        status: error.response?.status,
        data: error.response?.data,
      };

      console.error('Error API:', error?.request?._header, requestError);

      if (safe) return null as T;
      if (error.response?.data)
        throw {
          ...error.response?.data,
          data: error?.response?.data,
          status: error.response.status,
        };

      throw error;
    }
  }
}

interface Params {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  params?: any;
  headers?: any;
  safe?: boolean; // Se true, o método não irá lançar exceção caso não encontre cotação para o ativo, retornando null
  config?: any;
}
